import { Select } from "@adasupport/byron";
import { useFlags } from "launchdarkly-react-client-sdk";
import React from "react";

import { TitleWithSubtitle } from "components/Common/ByronSelectUtilities";
import { InputLabel } from "components/Common/InputLabel";
import {
  type CaptureMode,
  type InferredVariableType,
} from "services/webActions";

import { InputHint } from "../InputHint";

export function CaptureModeSelectorLabel() {
  return (
    <InputLabel
      label="Voice call capture options"
      tooltip={{
        blurb:
          "Applies only to Voice conversations. No impact on other channels.",
        iconDefault: "Info",
      }}
    />
  );
}

interface Props {
  selectedCaptureModes: CaptureMode[];
  dataType: InferredVariableType;
  onChange: (value: CaptureMode[]) => void;
  isDisabled?: boolean;
  hideDTMF?: boolean;
}

/**
 * For clients with the old architecture. Unlike the new version this component allows
 * sms only and dtmf only modes.
 * This component should be removed once all clients have been migrated to the new architecture.
 */
function OldCaptureModeSelector({
  selectedCaptureModes,
  dataType,
  onChange,
  isDisabled,
  hideDTMF = false,
}: Props) {
  const options = (
    [
      {
        label: (
          <TitleWithSubtitle
            title="Speech"
            description="The AI Agent will capture this input from the spoken conversation with the customer."
          />
        ),
        triggerLabel: "Speech",
        value: "conversation",
        isDisabled:
          selectedCaptureModes.includes("conversation") &&
          selectedCaptureModes.length === 1,
      },
      {
        label: (
          <TitleWithSubtitle
            title="SMS Capture"
            description="Use SMS sent to the customer's device to capture an input. This will only be used in Voice conversations."
          />
        ),
        triggerLabel: "SMS Capture",
        value: "sms",
        isDisabled:
          (selectedCaptureModes.includes("sms") &&
            selectedCaptureModes.length === 1) ||
          selectedCaptureModes.includes("dtmf"),
      },
      {
        label: (
          <TitleWithSubtitle
            title="DTMF Capture"
            description="Use dial tones captured from the keypad of the customer's device to capture an input. This will only be used in Voice conversations and is only available with the Number capture type."
          />
        ),
        triggerLabel: "DTMF Capture",
        value: "dtmf",
        isDisabled:
          (selectedCaptureModes.includes("dtmf") &&
            selectedCaptureModes.length === 1) ||
          selectedCaptureModes.includes("sms") ||
          dataType !== "number",
      },
    ] as const
  ).filter((option) => !(hideDTMF && option.value === "dtmf"));

  return (
    <>
      <Select
        value={selectedCaptureModes}
        isMultiSelect
        options={options}
        onChange={onChange}
        isDisabled={isDisabled}
      />
      {hideDTMF && (
        <InputHint>
          DTMF Capture will automatically be used for capturing numbers in Voice
          conversations (e.g. phone numbers).
        </InputHint>
      )}
    </>
  );
}

/**
 * Component for selecting voice call capture modes.
 * Always includes 'conversation' mode as it's the base mode for voice interactions.
 * Additional modes (SMS, DTMF) can be selected based on the data type and configuration:
 *    boolean: Speech
 *    text: Speech, Speech and SMS
 *    number: Speech, Speech and SMS, Speech and DTMF
 */
function NewCaptureModeSelector({
  selectedCaptureModes,
  dataType,
  onChange,
  isDisabled,
  hideDTMF = false,
}: Props) {
  const options = (
    [
      {
        label: (
          <TitleWithSubtitle
            title="Speech"
            description="Only captures spoken responses"
          />
        ),
        triggerLabel: "Speech",
        value: "conversation",
        isDisabled: false,
      },
      {
        label: (
          <TitleWithSubtitle
            title="Speech and SMS"
            description="Allow customers to respond by speaking or by sending text messages"
          />
        ),
        triggerLabel: "Speech and SMS",
        value: "sms",
        isDisabled: dataType === "boolean",
      },
      {
        label: (
          <TitleWithSubtitle
            title="Speech and DTMF"
            description="Allow customers to respond by speaking or by using the phone dial pad to input numbers"
          />
        ),
        triggerLabel: "Speech and DTMF",
        value: "dtmf",
        isDisabled: dataType !== "number" || hideDTMF,
      },
    ] as const
  ).filter((option) => !option.isDisabled);

  return (
    <>
      <Select
        value={
          selectedCaptureModes.find((m) => m !== "conversation") ??
          "conversation"
        }
        options={options}
        onChange={(value: CaptureMode) =>
          // as of VOICE-1771 we always include 'conversation'
          onChange(
            value === "conversation"
              ? ["conversation"]
              : [value, "conversation"],
          )
        }
        isDisabled={isDisabled || dataType === "boolean"}
      />
      {hideDTMF && (
        <InputHint>
          DTMF Capture will automatically be used for capturing numbers in Voice
          conversations (e.g. phone numbers).
        </InputHint>
      )}
    </>
  );
}

/**
 * Currently we support two different ui for the new and old architecture.
 * Once all clients have been migrated to the new architecture,
 * this component should be replaced with NewCaptureModeSelector
 */
export function CaptureModeSelector(props: Props) {
  const flags = useFlags();
  const isVoicePocVariant =
    flags["voice-poc-variant"] === "eap-in-voice-box" ||
    flags["voice-poc-variant"] === "eap-in-api";

  return isVoicePocVariant ? (
    <NewCaptureModeSelector {...props} />
  ) : (
    <OldCaptureModeSelector {...props} />
  );
}
